import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  XMarkIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShieldCheckIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';
import { useP2POrders, usePaymentMethods } from '../../hooks/useP2P';
import { formatNumber } from '../../utils/formatters';

const P2POrderModal = ({ isOpen, onClose, ad, type }) => {
  const [fiatAmount, setFiatAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [buyerPaymentInfo, setBuyerPaymentInfo] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const navigate = useNavigate();
  const { createOrder } = useP2POrders();
  const { paymentMethods } = usePaymentMethods();

  if (!isOpen || !ad) return null;

  // Calculate crypto amount based on fiat amount
  const cryptoAmount = fiatAmount && ad.price ? (parseFloat(fiatAmount) / ad.price) : 0;

  // Filter payment methods based on ad's accepted methods
  const availablePaymentMethods = paymentMethods.filter(method => 
    ad.payment_methods.some(adMethod => adMethod.id === method.id)
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous error
    setError('');
    
    if (!fiatAmount || !selectedPaymentMethod) {
      setError('Please fill in all required fields');
      return;
    }

    const amount = parseFloat(fiatAmount);
    if (amount < ad.min_order_amount || amount > ad.max_order_amount) {
      setError(`Amount must be between ${ad.min_order_amount} and ${ad.max_order_amount} ${ad.fiat_currency}`);
      return;
    }

    // Validate buyer payment info for sell orders (buyer is selling crypto)
    if (ad.type === 'sell' && !buyerPaymentInfo.trim()) {
      setError('Please provide your payment details');
      return;
    }

    setLoading(true);
    try {
      const orderData = {
        ad_id: ad.ad_id,
        fiat_amount: amount,
        payment_method_id: selectedPaymentMethod,
        message: message
      };

      // Add buyer payment info if they're selling crypto (responding to a buy ad)
      if (ad.type === 'sell') {
        orderData.buyer_payment_info = buyerPaymentInfo.trim();
      }

      const order = await createOrder(orderData);
      onClose();
      
      // Navigate to order details page
      navigate(`/p2p/order/${order.order_id}`);
    } catch (error) {
      // Error message is already displayed in toast by the hook
      // Set it here as well for display in the modal
      setError(error.message || 'Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-[#010615] rounded-xl border border-gray-700 max-w-lg sm:max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700">
          <h2 className="text-lg sm:text-xl font-bold text-white">
            {type === 'buy' ? 'Buy' : 'Sell'} {ad.asset}
          </h2>
          <button
            onClick={onClose}
            className="text-white hover:text-white transition-colors p-1"
          >
            <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
        </div>

        {/* Merchant Info */}
        <div className="p-4 sm:p-6 border-b border-gray-700">
          <div className="flex items-center space-x-3 sm:space-x-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white font-bold text-sm sm:text-base">
                {ad.user_profile.nickname.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <span className="text-white font-medium text-sm sm:text-base truncate">{ad.user_profile.nickname}</span>
                {ad.user_profile.is_verified && (
                  <ShieldCheckIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-400 flex-shrink-0" />
                )}
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-4 text-xs sm:text-sm text-white">
                <span>Rating: {ad.user_profile.rating.toFixed(1)}/5</span>
                <span>Trades: {ad.user_profile.total_trades}</span>
                <span>Completion: {ad.user_profile.completion_rate.toFixed(1)}%</span>
              </div>
            </div>
          </div>
        </div>

        {/* Order Form */}
        <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4 sm:space-y-6">
          {/* Error Message */}
          {error && (
            <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-3 sm:p-4">
              <div className="flex items-start space-x-2 sm:space-x-3">
                <XCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-red-400 mt-0.5 flex-shrink-0" />
                <div className="flex-1">
                  <h4 className="font-medium text-red-400 mb-1 text-sm sm:text-base">Error</h4>
                  <p className="text-xs sm:text-sm text-red-300">{error}</p>
                </div>
                <button
                  type="button"
                  onClick={() => setError('')}
                  className="text-red-400 hover:text-red-300 transition-colors"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}

          {/* Price Info */}
          <div className="bg-gray-800 rounded-lg p-3 sm:p-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-white text-xs sm:text-sm">Price</span>
              <span className="text-white font-medium text-sm sm:text-base">
                {formatNumber(ad.price)} {ad.fiat_currency}
              </span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-white text-xs sm:text-sm">Available</span>
              <span className="text-white text-sm sm:text-base">
                {formatNumber(ad.available_amount)} {ad.asset}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-white text-xs sm:text-sm">Limits</span>
              <span className="text-white text-sm sm:text-base">
                {formatNumber(ad.min_order_amount)} - {formatNumber(ad.max_order_amount)} {ad.fiat_currency}
              </span>
            </div>
          </div>

          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Amount ({ad.fiat_currency})
            </label>
            <input
              type="number"
              value={fiatAmount}
              onChange={(e) => setFiatAmount(e.target.value)}
              placeholder={`${ad.min_order_amount} - ${ad.max_order_amount}`}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              required
            />
            {cryptoAmount > 0 && (
              <p className="mt-2 text-xs sm:text-sm text-white">
                You will receive: {formatNumber(cryptoAmount)} {ad.asset}
              </p>
            )}
          </div>

          {/* Payment Method */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Payment Method
            </label>
            <select
              value={selectedPaymentMethod}
              onChange={(e) => setSelectedPaymentMethod(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              required
            >
              <option value="">Select payment method</option>
              {availablePaymentMethods.map(method => (
                <option key={method.id} value={method.id}>
                  {method.name}
                </option>
              ))}
            </select>
          </div>

          {/* Buyer Payment Info - Only for sell ads (buyer is selling crypto) */}
          {ad.type === 'sell' && (
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Your Payment Details <span className="text-red-400">*</span>
              </label>
              <textarea
                value={buyerPaymentInfo}
                onChange={(e) => setBuyerPaymentInfo(e.target.value)}
                placeholder="Enter your bank details, account number, PayPal email, or any payment info the merchant needs to send you money..."
                rows={4}
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-none"
                required
              />
              <p className="mt-2 text-xs text-white">
                ⚠️ Provide accurate payment details. The merchant will send payment to these details.
              </p>
            </div>
          )}

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-white mb-2">
              Message (Optional)
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a message to the seller..."
              rows={3}
              className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 sm:px-4 py-2 sm:py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
          </div>

          {/* Terms */}
          {ad.terms_and_conditions && (
            <div className="bg-yellow-900/20 border border-[#2A356D]/30 rounded-lg p-3 sm:p-4">
              <div className="flex items-start space-x-2 sm:space-x-3">
                <ExclamationTriangleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-white mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-white mb-1 text-sm sm:text-base">Merchant Terms</h4>
                  <p className="text-xs sm:text-sm text-white">{ad.terms_and_conditions}</p>
                </div>
              </div>
            </div>
          )}

          {/* Payment Time Limit */}
          <div className="bg-blue-900/20 border border-blue-600/30 rounded-lg p-3 sm:p-4">
            <div className="flex items-start space-x-2 sm:space-x-3">
              <InformationCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-blue-400 mb-1 text-sm sm:text-base">Payment Window</h4>
                <p className="text-xs sm:text-sm text-white">
                  You have {ad.payment_time_limit_minutes} minutes to complete the payment after creating this order.
                </p>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 sm:px-6 py-2 sm:py-3 border border-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !fiatAmount || !selectedPaymentMethod}
              className="flex-1 px-4 sm:px-6 py-2 sm:py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all text-sm"
            >
              {loading ? 'Creating Order...' : `${type === 'buy' ? 'Buy' : 'Sell'} ${ad.asset}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default P2POrderModal;
